import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { eachDayOfInterval, format, parseISO } from "date-fns";
import { ClipboardClockIcon, Loader2 } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import UpdateTimeSheetLog from "@/v2/api/time-sheet/UpdateTimeSheetLog";
import FindOneTimeSheetLog, {
    TimeSheetDetails,
    TimesheetEntry,
    WorkLogs,
} from "@/v2/api/time-sheet/FindOneTimeSheetLog";

import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import TimeSheetStatusActionBtn from "@/v2/components/pages/time-sheet/TimeSheetStatusActionBtn";
import FormPageHeading from "@/v2/components/ui/heading/FormPage";
import LoadingAnimation from "@/v2/components/ui/LoadingAnimation";
import AddCommentDialog from "@/v2/components/pages/time-sheet/dialog/AddComment";
import AddOtherDeductionDialog from "@/v2/components/pages/time-sheet/dialog/AddOtherDedcution";
import TimeSheetStatusBadge from "@/v2/components/pages/time-sheet/TimeSheetStatusBadge";

const STATUS_COLOR: Record<WorkLogs["status"], string> = {
    PRESENT: "bg-green-100 text-green-800 border-green-200",
    ABSENT: "bg-red-100 text-red-800 border-red-200",
    SICK_LEAVE: "bg-orange-100 text-orange-800 border-orange-200",
    HOLIDAY: "bg-yellow-100 text-yellow-800 border-yellow-200",
    NOT_MOBILIZED: "bg-gray-100 text-gray-500 border-gray-200",
    DAY_OFF: "bg-blue-100 text-blue-800 border-blue-200",
    RELEASED: "bg-slate-200 text-slate-600 border-slate-300",
    VACATION: "bg-pink-100 text-pink-800 border-pink-200",
    PUNCH_MISS: "bg-purple-100 text-purple-800 border-purple-200",
    OTHER: "bg-gray-300 text-black ",
};

const STATUS_LABELS: Record<WorkLogs["status"], string> = {
    PRESENT: "Present",
    ABSENT: "Absent",
    SICK_LEAVE: "Sick Leave",
    HOLIDAY: "Holiday",
    NOT_MOBILIZED: "Not Mobilized",
    DAY_OFF: "Day Off",
    RELEASED: "Released",
    VACATION: "Vacation",
    PUNCH_MISS: "Punch Miss",
    OTHER: "Other",
};

type SelectableStatus = Exclude<
    WorkLogs["status"],
    "NOT_MOBILIZED" | "RELEASED"
>;
const SELECTABLE_STATUSES: SelectableStatus[] = [
    "ABSENT",
    "SICK_LEAVE",
    "HOLIDAY",
    "DAY_OFF",
    "VACATION",
    "PUNCH_MISS",
    "OTHER",
];

const HOUR_OPTIONS = Array.from({ length: 40 }, (_, i) => (i + 1) * 0.5);

const MemoizedSelectOptions = memo(() => {
    return (
        <>
            <optgroup label="Hours" className="bg-white text-black">
                {HOUR_OPTIONS.map((val) => (
                    <option
                        key={val}
                        value={val}
                        className="bg-white text-black"
                    >
                        {val.toFixed(1)} Hours
                    </option>
                ))}
            </optgroup>
            <optgroup label="Status" className="bg-white text-black">
                {SELECTABLE_STATUSES.map((val) => (
                    <option
                        key={val}
                        value={val}
                        className="bg-white text-black"
                    >
                        {STATUS_LABELS[val]}
                    </option>
                ))}
            </optgroup>
        </>
    );
});

MemoizedSelectOptions.displayName = "MemoizedSelectOptions";

const MemoizedTimeSheetRow = memo(
    ({
        entry,
        timeSheetId,
    }: {
        entry: TimesheetEntry;
        timeSheetId: string;
    }) => {
        const queryClient = useQueryClient();

        const cellMutation = useMutation({
            mutationKey: ["time-sheets-logs", timeSheetId],
            mutationFn: UpdateTimeSheetLog,
            onMutate: async (variables) => {
                await queryClient.cancelQueries({
                    queryKey: ["time-sheets-logs", timeSheetId],
                });

                const previousData = queryClient.getQueryData([
                    "time-sheets-logs",
                    timeSheetId,
                ]);

                queryClient.setQueryData(
                    ["time-sheets-logs", timeSheetId],
                    (oldData: {
                        logs: TimesheetEntry[] | undefined;
                        details: TimeSheetDetails | undefined;
                    }) => {
                        if (!oldData) return oldData;

                        return {
                            ...oldData,
                            logs: oldData.logs?.map((e) => {
                                if (e.id !== entry.id) return e;

                                const updatedLogs = e.work_logs.map((log) => {
                                    if (log.id === variables.cellId) {
                                        return {
                                            ...log,
                                            hours: variables.hours,
                                            status: variables.status,
                                        };
                                    }
                                    return log;
                                });

                                const totalHours = updatedLogs.reduce(
                                    (sum, log) =>
                                        log.status === "PRESENT"
                                            ? sum + log.hours
                                            : sum,
                                    0,
                                );

                                return {
                                    ...e,
                                    work_logs: updatedLogs,
                                    total_hours: totalHours,
                                };
                            }),
                        };
                    },
                );

                return { previousData };
            },
            onSuccess: (updatedCell) => {
                queryClient.setQueryData(
                    ["time-sheets-logs", timeSheetId],
                    (oldData: {
                        logs: TimesheetEntry[] | undefined;
                        details: TimeSheetDetails | undefined;
                    }) => {
                        if (!oldData) return oldData;

                        return {
                            logs: oldData.logs?.map((entry) => {
                                const logIndex = entry.work_logs.findIndex(
                                    (l) => l.id === updatedCell.cellId,
                                );

                                if (logIndex === -1) return entry;

                                const updatedLogs = [...entry.work_logs];
                                updatedLogs[logIndex] = {
                                    ...updatedLogs[logIndex],
                                    hours: updatedCell.hours,
                                    status: updatedCell.status,
                                };

                                const totalHours = updatedLogs.reduce(
                                    (sum, log) =>
                                        log.status === "PRESENT"
                                            ? sum + log.hours
                                            : sum,
                                    0,
                                );

                                return {
                                    ...entry,
                                    work_logs: updatedLogs,
                                    total_hours: totalHours,
                                };
                            }),
                            details: oldData?.details,
                        };
                    },
                );
            },
            onError: (_, __, context) => {
                queryClient.setQueryData(
                    ["time-sheets-logs", timeSheetId],
                    context?.previousData,
                );
            },
        });

        const handleChange = useCallback(
            ({
                event,
                cellId,
            }: {
                event: React.ChangeEvent<HTMLSelectElement>;
                cellId: string;
            }) => {
                const rawValue = event.target.value;
                const numericValue = Number(rawValue);
                const isNumeric = !isNaN(numericValue);

                const hours = isNumeric ? numericValue : 0;
                const status = isNumeric ? "PRESENT" : rawValue;

                cellMutation.mutate({
                    cellId: cellId,
                    hours: hours,
                    status: status as WorkLogs["status"],
                });
            },
            [cellMutation],
        );

        return (
            <TableRow>
                <TableCell
                    className="sticky left-0 z-10 min-w-[240px] max-w-[240px] truncate whitespace-nowrap border-r bg-white p-0 px-4 font-medium"
                    title={entry.employee_name}
                >
                    <div className="w-full">{entry.employee_name}</div>
                    <div className="text-sm text-gray-500">{entry.iqama}</div>
                </TableCell>
                <TableCell className="sticky left-[239px] z-10 truncate whitespace-nowrap border-r bg-white p-0 px-4 font-medium">
                    <div className="w-full">{entry.trade}</div>
                </TableCell>
                {entry.work_logs?.map((log) => {
                    const isUpdating =
                        cellMutation.isPending &&
                        cellMutation.variables?.cellId === log.id;

                    const selectedValue =
                        log.status == "PRESENT"
                            ? log.hours.toString()
                            : log.status;

                    return (
                        <TableCell
                            key={log.id}
                            className="whitespace-nowrap border p-0 text-center"
                        >
                            {isUpdating ? (
                                <div className="flex items-center justify-center">
                                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                </div>
                            ) : ["NOT_MOBILIZED", "RELEASED"].includes(
                                  log.status,
                              ) ? (
                                <p className="flex min-h-[60px] min-w-[120px] items-center justify-center bg-gray-500">
                                    {STATUS_LABELS[log.status]}
                                </p>
                            ) : (
                                <select
                                    id={log.id}
                                    defaultValue={selectedValue}
                                    onChange={(e) =>
                                        handleChange({
                                            event: e,
                                            cellId: log.id,
                                        })
                                    }
                                    className={`min-h-[60px] min-w-[120px] ${STATUS_COLOR[log.status]} text-center`}
                                >
                                    <MemoizedSelectOptions />
                                </select>
                            )}
                        </TableCell>
                    );
                })}

                <TableCell className="sticky right-0 z-10 min-h-[60px] w-[120px] border-r bg-white text-center font-medium">
                    {entry.total_hours}
                </TableCell>
            </TableRow>
        );
    },
);

MemoizedTimeSheetRow.displayName = "MemoizedTimeSheetRow";

function MonthDays({
    endDate,
    startDate,
}: {
    startDate: string | undefined;
    endDate: string | undefined;
}) {
    if (!endDate || !startDate) {
        return null;
    }

    const start = parseISO(startDate);
    const end = parseISO(endDate);

    const dayRange = eachDayOfInterval({ start, end });
    const formattedDays = dayRange.map((date) => ({
        weekDay: format(date, "EEEE"),
        day: format(date, "dd"),
    }));

    return (
        <>
            {formattedDays.map((data, idx) => {
                return (
                    <TableCell
                        key={idx}
                        className={`sticky top-0 min-w-[120px] bg-gray-100 text-center ${data?.weekDay == "Friday" ? "border-yellow-200 bg-yellow-100 text-yellow-800" : ""}`}
                    >
                        <p>{data?.weekDay}</p>
                        <p>{data?.day}</p>
                    </TableCell>
                );
            })}
        </>
    );
}

const MemoizedHeaderRow = memo(
    ({ startDate, endDate }: { startDate: string; endDate: string }) => {
        return (
            <TableRow>
                <TableCell className="sticky left-0 top-0 z-20 min-w-[240px] max-w-[240px] border-r bg-gray-100 font-bold">
                    Employee Name
                </TableCell>
                <TableCell className="sticky left-[239px] top-0 z-20 min-w-[120px] bg-gray-100 font-bold">
                    Employee Trade
                </TableCell>
                <MonthDays startDate={startDate} endDate={endDate} />

                <TableCell className="sticky right-0 top-0 z-20 min-h-[60px] min-w-[80px] border-r bg-gray-100 text-center font-bold">
                    Total Hours
                </TableCell>
            </TableRow>
        );
    },
);

const OtherDeductions = memo(
    ({
        initialAmount,
        initialReason,
        onRegisterUpdate,
    }: {
        initialAmount: number;
        initialReason: string;
        onRegisterUpdate: (
            fn: (data: { amount: number; reason: string }) => void,
        ) => void;
    }) => {
        const [displayData, setDisplayData] = useState({
            amount: initialAmount,
            reason: initialReason,
        });

        // Register the update function with the parent on mount
        useEffect(() => {
            onRegisterUpdate((newData) => setDisplayData(newData));
        }, [onRegisterUpdate]);

        return (
            <div className="space-y-2">
                <h3 className="font-semibold text-gray-900">
                    Other Deductions
                </h3>
                <div className="space-y-2">
                    <div className="flex justify-between">
                        <p className="text-gray-700">Amount:</p>
                        <p className="font-bold text-red-600">
                            {displayData.amount.toFixed(2)} SAR
                        </p>
                    </div>
                    <div className="border-t pt-2">
                        <p className="mb-1 text-sm font-medium text-gray-700">
                            Reason:
                        </p>
                        <p className="text-sm text-gray-600">
                            {displayData.reason || "N/A"}
                        </p>
                    </div>
                </div>
            </div>
        );
    },
);

export default function ViewOneTimeSheetLog() {
    const params = useParams();
    const timeSheetId = params?.timeSheetId || "";

    const query = useQuery({
        queryKey: ["time-sheets-logs", timeSheetId],
        queryFn: () =>
            FindOneTimeSheetLog({
                timeSheetId: timeSheetId,
            }),
        // Cache is kept in sync by the optimistic cell updates; a focus
        // refetch would overwrite in-progress edits.
        refetchOnWindowFocus: false,
    });

    const currentMonth = useMemo(() => {
        const startDate = query?.data?.details?.start_date;
        if (startDate) {
            const date = parseISO(startDate);
            return format(date, "MMMM yyyy");
        }
    }, [query?.data?.details?.start_date]);

    const calculations = useMemo(() => {
        if (!query.data?.logs || !query.data?.details) {
            return {
                totalHours: 0,
                totalAmount: 0,
                deductionAmount: 0,
                grandTotal: 0,
            };
        }

        const logs = query.data.logs;
        const project = query.data.details.project;

        // Calculate total hours and total amount
        const totalHours = logs.reduce(
            (sum, entry) => sum + entry.total_hours,
            0,
        );

        const totalAmount = logs.reduce(
            (sum, entry) => sum + entry.total_hours * entry.hourly_rate,
            0,
        );

        // Calculate deduction amount (only if project.deduction is true)
        let deductionAmount = 0;
        if (project?.deduction && project?.deduction_amount) {
            deductionAmount = logs.reduce((sum, entry) => {
                const absentDays = entry.work_logs.filter(
                    (log) => log.status === "ABSENT",
                ).length;
                return sum + absentDays * (project.deduction_amount || 0);
            }, 0);
        }

        const otherDeductions = query.data.details.other_deductions || 0;

        const grandTotal = totalAmount - deductionAmount - otherDeductions;

        return {
            totalHours,
            totalAmount,
            deductionAmount,
            grandTotal,
        };
    }, [query.data]);

    const deductionAmountRef = useRef(0);
    const deductionReasonRef = useRef("");

    useEffect(() => {
        if (query.data?.details) {
            deductionAmountRef.current = query.data.details.other_deductions;
            deductionReasonRef.current = query.data.details.deduction_note;
        }
    }, [query.data]);

    const updateDisplayRef = useRef<
        ((data: { amount: number; reason: string }) => void) | null
    >(null);

    const handleCaptureDeductions = useCallback(
        (data: { amount: number; reason: string }) => {
            deductionAmountRef.current = data.amount;
            deductionReasonRef.current = data.reason;

            updateDisplayRef.current?.(data);
        },
        [],
    );

    if (query.isLoading) {
        return <LoadingAnimation />;
    }

    if (!query.data) return <p>Something went wrong</p>;

    return (
        <>
            <Card>
                <FormPageHeading
                    title={"Edit Time Sheet "}
                    description="Edit time sheet for the mobilized employees"
                    Icon={ClipboardClockIcon}
                />
                <CardContent className="flex flex-col">
                    <div className="flex items-center gap-3 py-4">
                        <h2 className="text-2xl font-bold">
                            Timesheet - {currentMonth}
                        </h2>
                        <TimeSheetStatusBadge
                            status={query?.data?.details?.status}
                        />
                    </div>

                    <div
                        className="relative w-full overflow-auto"
                        style={{ height: "calc(100vh - 475px)" }}
                    >
                        <Table>
                            <TableBody>
                                <MemoizedHeaderRow
                                    startDate={query?.data?.details?.start_date}
                                    endDate={query?.data?.details?.end_date}
                                />
                                {query?.data?.logs?.map((entry) => (
                                    <MemoizedTimeSheetRow
                                        key={entry.id}
                                        entry={entry}
                                        timeSheetId={timeSheetId}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <div className="flex items-start justify-between space-x-2 py-4">
                        <div className="flex w-full items-center justify-start gap-2 space-y-2">
                            {/* Summary Calculations */}
                            <div className="w-full rounded-md border border-gray-200 bg-gray-50 p-4">
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                    <div className="space-y-2 border-r pr-4">
                                        <h3 className="mb-3 font-semibold text-gray-900">
                                            Timesheet Summary
                                        </h3>
                                        <div className="flex justify-between">
                                            <p className="text-gray-700">
                                                Total Hours:
                                            </p>
                                            <p className="font-bold text-gray-900">
                                                {calculations.totalHours.toFixed(
                                                    2,
                                                )}{" "}
                                                Hours
                                            </p>
                                        </div>
                                        <div className="flex justify-between">
                                            <p className="text-gray-700">
                                                Total Amount:
                                            </p>
                                            <p className="font-bold text-green-600">
                                                {calculations.totalAmount.toFixed(
                                                    2,
                                                )}{" "}
                                                SAR
                                            </p>
                                        </div>
                                        <div className="flex justify-between gap-1 border-t pt-2">
                                            <p className="text-gray-700">
                                                Deduction (Absents) :
                                            </p>
                                            <p className="font-bold text-red-600">
                                                {query?.data?.details?.project
                                                    ?.deduction
                                                    ? `${calculations.deductionAmount.toFixed(
                                                          2,
                                                      )} SAR`
                                                    : "Not subjected to dedcution"}
                                            </p>
                                        </div>
                                        <div className="flex justify-between gap-1 border-t-2 border-gray-800 pt-3">
                                            <p className="text-lg font-bold text-gray-900">
                                                Grand Total:
                                            </p>
                                            <p className="text-lg font-bold text-blue-600">
                                                {calculations.grandTotal.toFixed(
                                                    2,
                                                )}{" "}
                                                SAR
                                            </p>
                                        </div>
                                    </div>
                                    <OtherDeductions
                                        initialAmount={
                                            query.data.details.other_deductions
                                        }
                                        initialReason={
                                            query.data.details.deduction_note
                                        }
                                        onRegisterUpdate={(fn) =>
                                            (updateDisplayRef.current = fn)
                                        }
                                    />
                                    <div className="border-l pl-4">
                                        <h3 className="mb-3 font-semibold text-gray-900">
                                            Comment
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                            {query?.data?.details?.note ||
                                                "N/A"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex space-x-2">
                                <AddCommentDialog
                                    timeSheetId={timeSheetId}
                                    timeSheetDetails={query?.data?.details}
                                />
                                <AddOtherDeductionDialog
                                    initialAmount={
                                        query.data.details.other_deductions
                                    }
                                    initialReason={
                                        query.data.details.deduction_note
                                    }
                                    onSave={handleCaptureDeductions}
                                    timeSheetId={timeSheetId}
                                    timeSheetDetails={query?.data?.details}
                                />
                            </div>
                            <TimeSheetStatusActionBtn
                                status="TO_BE_VERIFIED"
                                timeSheetId={timeSheetId}
                                timeSheetDetails={query?.data?.details}
                                getDeductionData={() => ({
                                    other_deductions:
                                        deductionAmountRef.current,
                                    deduction_note: deductionReasonRef.current,
                                })}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>
        </>
    );
}
